/**
 * @file NextDeadlineCallout.tsx
 * @description Slice 78 — surfaces the customer's soonest-upcoming
 *   in-flight project deadline as a single-line callout above the
 *   project list. The per-row chip from slice 69 already shows the
 *   same data, but scanning a list to find "what's most urgent next?"
 *   is a real task this saves. Hidden when no in-flight project has a
 *   deadline within WINDOW_DAYS or when all candidates are overdue
 *   (overdue gets its own red-chip treatment in slice 72 — duplicating
 *   here would be noisy).
 * @module Components
 * @security Pure UI on top of data the dashboard already fetched —
 *   no extra API call, no PII beyond what's already on screen.
 */

import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { daysUntil } from '../../lib/date-helpers';
import type { ProjectDto, ProjectStatus } from '../../lib/projects-api';

const WINDOW_DAYS = 14;
const TERMINAL: ReadonlySet<ProjectStatus> = new Set(['completed', 'cancelled']);

interface Candidate {
  project: ProjectDto;
  days: number;
}

/** Pure — exported for tests. Picks the candidate with the smallest
 *  non-negative `daysUntil` ≤ WINDOW_DAYS among in-flight, not-archived
 *  projects with a deadline set. Returns null if nothing qualifies.
 *  Overdue projects (days < 0) are intentionally excluded — slice 72's
 *  red chip already handles those, and the callout's "next" framing
 *  reads weirdly for the past. */
export function pickNextDeadline(projects: ProjectDto[], now?: Date): Candidate | null {
  let best: Candidate | null = null;
  for (const p of projects) {
    if (p.archived_at !== null) continue;
    if (TERMINAL.has(p.status)) continue;
    if (p.desired_deadline === null) continue;
    const days = daysUntil(p.desired_deadline, now);
    if (days === null) continue;
    if (days < 0 || days > WINDOW_DAYS) continue;
    if (best === null || days < best.days) best = { project: p, days };
  }
  return best;
}

interface Props {
  projects: ProjectDto[];
}

export function NextDeadlineCallout({ projects }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const pick = pickNextDeadline(projects);
  if (pick === null) return null;
  const label =
    pick.days === 0 ? t('next_deadline.today') : t('next_deadline.in_n_days', { count: pick.days });
  const tone =
    pick.days <= 1
      ? 'bg-red-50 text-red-900 border-red-200'
      : pick.days <= 7
        ? 'bg-amber-50 text-amber-900 border-amber-200'
        : 'bg-blue-50 text-blue-900 border-blue-200';
  return (
    <Link
      to={`/projects/${pick.project.id}`}
      className={`block rounded-lg border px-3 py-2 text-sm shadow-card hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${tone}`}
    >
      <span className="font-semibold">{t('next_deadline.label')}:</span> <span>{label}</span>
      <span className="opacity-70"> · </span>
      <span className="font-medium">{pick.project.title}</span>
    </Link>
  );
}
